import React, { useEffect, useState } from "react";
import Card from "components/card";

import { generaleTableApi, generaleTableXcelApi } from "api/dashboard";
import { Spinner } from "@material-tailwind/react";
import PieChart from "components/charts/PieChart";
import BarChart from "components/charts/BarChart";
import RadarChart from "components/charts/RadarChart";
import API_URL from "config";

const GlobaleTable = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingXcel, setIsLoadingXcel] = useState(false);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [tableData, setTableData] = useState([]);

  const getInitialData = () => {
    try {
      setIsLoading(true);
      if (fromDate && toDate) {
        generaleTableApi(fromDate, toDate)
        .then((generaleTableRes) => {
          setTableData(generaleTableRes);
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
      } else {
        generaleTableApi(null, null)
        .then((generaleTableRes) => {
          setTableData(generaleTableRes);
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          setIsLoading(false)
        })
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleFilterData = () => {
    try {
      getInitialData();
    }
    catch (err) {
      
    }
  }

  const handleDownloadXcel = async () => {
    try {
      setIsLoadingXcel(true);
      if (fromDate && toDate) {
        const generalXcelTableRes = await generaleTableXcelApi(
          fromDate,
          toDate
        );

        window.open(`${API_URL}/${generalXcelTableRes}`, "_blank");
      } else {
        const generalXcelTableRes = await generaleTableXcelApi(null, null);

        window.open(`${API_URL}/${generalXcelTableRes}`, "_blank");
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setIsLoadingXcel(false);
    }
  };

  // Format number with space as thousand separators
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-EN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Build aggregates for charts from generale table data
  const getCategoryPieData = () => {
    if (!tableData?.data) return { series: [], labels: [] };

    const categoryTotals = new Map();
    Object.entries(tableData.data).forEach(([tcp, products]) => {
      Object.entries(products).forEach(([productName, entries]) => {
        const category = entries?.[0]?.Category || 'Autre';
        const amount = parseFloat(entries[entries.length - 1]?.sumOfAmount || 0);
        categoryTotals.set(category, (categoryTotals.get(category) || 0) + amount);
      });
    });

    const labels = Array.from(categoryTotals.keys());
    const series = labels.map((l) => categoryTotals.get(l));
    return { labels, series };
  };

  const getTopProductsBarData = () => {
    if (!tableData?.data) return { series: [], categories: [] };
    const totals = [];
    Object.entries(tableData.data).forEach(([tcp, products]) => {
      Object.entries(products).forEach(([productName, entries]) => {
        const amount = parseFloat(entries[entries.length - 1]?.sumOfAmount || 0);
        totals.push({ name: productName, amount });
      });
    });
    const top10 = totals.sort((a, b) => b.amount - a.amount).slice(0, 10);
    return {
      series: [{ name: 'Montant HT', data: top10.map((t) => t.amount) }],
      categories: top10.map((t) => t.name),
    };
  };

  const getPieOptions = () => ({
    chart: { type: 'pie', height: 380, width: '100%' },
    labels: getCategoryPieData().labels,
    legend: { position: 'bottom' },
    tooltip: {
      enabled: true,
      theme: 'dark',
      style: {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        background: '#000000'
      },
      y: {
        formatter: function (val) {
          return formatNumber(val) + " DH";
        }
      },
      fillSeriesColor: false,
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        const categoryName = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];
        const total = series.reduce((a, b) => a + b, 0);
        const percentage = ((value / total) * 100).toFixed(1);
        
        return '<div class="custom-tooltip" style="background: #000000; color: #ffffff; padding: 10px 15px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.3);">' +
          '<div style="font-weight: 600; margin-bottom: 5px;">' + categoryName + '</div>' +
          '<div style="font-size: 20px; font-weight: bold">' + formatNumber(value) + ' DH</div>' +
          '<div style="font-size: 18px; color: #9CA3AF; margin-top: 3px; font-weight: bold">' + percentage + '%</div>' +
          '</div>';
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val, opts) => {
        const amount = formatNumber(opts.w.config.series[opts.seriesIndex]);
        return `${amount} DH\n(${val.toFixed(1)}%)`;
      },
      style: {
        fontSize: '20px',
        fontWeight: 500,
        colors: ['#000000']
      },
      background: {
        enabled: true,
        padding: 6,
        borderRadius: 8,
        borderWidth: 0,
        opacity: 0.92,
        dropShadow: { enabled: false }
      }
    },
  });

  const getBarOptions = () => {
    const { categories } = getTopProductsBarData();
    return {
      chart: { type: 'bar', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '60%' } },
      xaxis: { labels: { formatter: (v) => formatNumber(v) }, title: { text: 'Montant HT (DH)' } },
      yaxis: { categories, labels: { style: { fontSize: '12px' } } },
      dataLabels: { 
        enabled: true,
        formatter: (val) => `${formatNumber(val)} DH`,
        offsetX: 8,
        style: { 
          fontSize: '13px',
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false }
        },
      },
      tooltip: { y: { formatter: (v) => `${formatNumber(v)} DH` } },
      colors: ['#236571'],
      grid: { strokeDashArray: 4 },
    };
  };

  // Radar: compare Quantité vs Montant HT across top TCPs (by amount)
  const getTopTcpRadarData = () => {
    if (!tableData?.data) return { categories: [], qtySeries: [], amtSeries: [] };

    const aggregates = Object.entries(tableData.data).map(([tcp, products]) => {
      // Sum across products for this TCP
      let qty = 0; let amt = 0;
      Object.entries(products).forEach(([name, entries]) => {
        qty += parseFloat(entries[entries.length - 1]?.sumOfQuantity || 0);
        amt += parseFloat(entries[entries.length - 1]?.sumOfAmount || 0);
      });
      return { tcp, qty, amt };
    });

    const top = aggregates.sort((a,b) => b.amt - a.amt).slice(0, 8);
    const categories = top.map(t => t.tcp);

    // Normalize values 0-100 for radar readability
    const maxQty = Math.max(1, ...top.map(t => t.qty));
    const maxAmt = Math.max(1, ...top.map(t => t.amt));
    const qtySeries = top.map(t => Math.round((t.qty / maxQty) * 100));
    const amtSeries = top.map(t => Math.round((t.amt / maxAmt) * 100));

    return { categories, qtySeries, amtSeries };
  };

  const getRadarOptions = () => {
    const { categories } = getTopTcpRadarData();
    return {
      chart: { 
        type: 'radar', 
        toolbar: { show: false }, 
        width: '100%', 
        height: 600 
      },
      xaxis: { categories },
      dataLabels: { 
        enabled: true,
        style: {
          fontSize: '15px',
          fontWeight: 600,
          colors: ['#374151']
        },
        background: {
          enabled: true,
          borderRadius: 6,
          borderWidth: 0,
          opacity: 0.85,
          dropShadow: { enabled: false }
        }
      },
      stroke: { width: 3 },
      fill: { opacity: 0.3 },
      legend: { 
        position: 'bottom',
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif'
      },
      yaxis: {
        show: true,
        labels: {
          formatter: (val) => `${val}%`,
          style: {
            fontSize: '12px'
          }
        }
      },
      responsive: [{
        breakpoint: 1200,
        options: {
          chart: {
            height: 500
          }
        }
      }, {
        breakpoint: 768,
        options: {
          chart: {
            height: 450
          }
        }
      }]
    };
  };

  useEffect(() => {
    getInitialData();
  }, []); // fromDate, toDate

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner className="h-12 w-12" />
        </div>
      ) : (
        <Card extra={"w-full pb-10 p-4 h-full"}>
          <header className="relative flex items-center justify-between">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              TABLE GENERALE
            </div>
            {isLoadingXcel ? (
              <div className="flex items-center justify-center">
                <Spinner className="h-12 w-12" />
              </div>
            ) : (
              <button
                href=""
                className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
                onClick={handleDownloadXcel}
              >
                Télécharger excel
              </button>
            )}
          </header>

          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-4">
            <div>
              <small className="text-sm font-medium text-gray-600 dark:text-white">
                Filtrer par date
              </small>
              <div>
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
                <input
                  type="date"
                  id=""
                  placeholder=""
                  className={`mt-2 flex w-full items-center justify-center rounded-xl border bg-white/0 p-2 text-sm outline-none`}
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-4">
            <button
              href=""
              className="linear rounded-[10px] bg-green-500 px-4 py-2 text-base font-medium text-white transition duration-200 hover:bg-green-400 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90"
              onClick={handleFilterData}
            >
              Filtrer
            </button>
          </div>

          {/* Charts for a simple overview */}
          {tableData?.data && (
            <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
              <Card extra={"w-full p-4"}>
                <div className="text-lg font-bold text-navy-700 dark:text-white mb-2">Répartition par Catégorie</div>
                <PieChart
                  series={getCategoryPieData().series}
                  options={getPieOptions()}
                  width="100%"
                  height={400}
                />
              </Card>
              <Card extra={"w-full p-4"}>
                <div className="text-lg font-bold text-navy-700 dark:text-white mb-2">Top 10 Produits par Montant HT</div>
                <BarChart
                  chartData={getTopProductsBarData().series}
                  chartOptions={getBarOptions()}
                  width="100%"
                  height={420}
                />
              </Card>
            </div>
          )}

          {tableData?.data && (
            <div className="mt-5">
              <Card extra={"w-full p-6"}>
                <div className="text-xl font-bold text-navy-700 dark:text-white mb-4">Comparaison TCP (Radar)</div>
                {(() => {
                  const { qtySeries, amtSeries } = getTopTcpRadarData();
                  const series = [
                    { name: 'Quantité', data: qtySeries },
                    { name: 'Montant HT', data: amtSeries },
                  ];
                  return (
                    <div className="w-full">
                      <RadarChart
                        series={series}
                        options={getRadarOptions()}
                        width="100%"
                        height={650}
                      />
                    </div>
                  );
                })()}
              </Card>
            </div>
          )}

          <div className="mt-8 overflow-x-scroll xl:overflow-x-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">TCP</p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">
                      Produit
                    </p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] text-start dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600">
                      Catégorie
                    </p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">
                      Quantité
                    </p>
                  </th>
                  <th className="border-b border-gray-200 pr-28 pb-[10px] dark:!border-navy-700">
                    <p className="text-xs tracking-wide text-gray-600 text-right">
                      Montant HT
                    </p>
                  </th>
                </tr>
              </thead>

              <tbody>
                {tableData?.data
                  ? Object.entries(tableData?.data).map(([key, value]) => {
                      const subQty = 0;
                      const subTotal = 0;

                      const subQtySubTotal = {
                        subQty: 0,
                        subTotal: 0
                      }
                      
                      return (
                        <>
                          {Object.entries(value).map(([key2, value2]) => {
                            const category = value2[0].Category;

                            const subSubQty = value2[value2.length - 1].sumOfQuantity
                            const subSubTotal = value2[value2.length - 1].sumOfAmount

                            subQtySubTotal.subQty = value2[value2.length - 1].sumOfOneTcpQuantity
                            subQtySubTotal.subTotal = value2[value2.length - 1].sumOfOneTcpAmount

                            // Object.entries(value).map(([index, product]) => {
                            //   subQty = parseFloat(product[product.length - 1].sumOfOneTcpQuantity)
                            //   subTotal = parseFloat(product[product.length - 1].sumOfOneTcpAmount)
                            //   // product.map((el) => {
                            //   //   subQty += parseFloat(el.Quantity);
                            //   //   subTotal += parseFloat(el.Amount);
                            //   // });
                            // });
                            return (
                              <tr>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {key}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {key2}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    {category}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                    {formatNumber(subSubQty.toFixed(2))}
                                  </p>
                                </td>
                                <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                                  <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                    {formatNumber(subSubTotal.toFixed(2))}
                                  </p>
                                </td>
                              </tr>
                            );
                          })}

                          <tr className="bg-gray-100 dark:bg-brand-400">
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                {formatNumber(subQtySubTotal.subQty.toFixed(2))}
                              </p>
                            </td>
                            <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                              <p className="text-sm font-bold text-navy-700 dark:text-white text-right mr-1">
                                {formatNumber(subQtySubTotal.subTotal.toFixed(2))}
                              </p>
                            </td>
                          </tr>
                        </>
                      );
                    })
                  : ""}

                <tr className="mt-5 bg-gray-200 dark:bg-brand-400">
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-sm font-bold text-navy-700 dark:text-white"></p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfQuantity?.toFixed(2))}
                    </p>
                  </td>
                  <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                    <p className="text-[22px] text-sm font-bold text-navy-700 dark:text-white text-right mr-1 font-mono">
                      {formatNumber(tableData?.sumOfAmount?.toFixed(2))}
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
};

export default GlobaleTable;
